/**
 * Tee-time tile for the guest booking results grid (compact card — hero row + course + spots badge).
 * For the table / Make-style layout use `TeeTimeListRow` via results list presentation.
 */
import React, { useState } from 'react';
import {
  bookingBrandFontFamilyVar,
  bookingBrandInfoChromeBorderMixPercent,
  bookingBrandInfoChromeStyles,
  bookingBrandInfoTintMixDefaultPercent,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import { headingTextStyle } from '../../utils/booking-brand-colors';
import { formatMoney } from '../../utils/format-money';
import {
  DemandTrendArrowIcon,
  PromoPricingGlyphIcon,
} from './TeeTimePricingMeterGlyphs';
import {
  formatStaffPromoLine,
  formatStaffPromoMeterHeadline,
  formatTime12hSingleLine,
  splitTime12h,
} from './tee-time-ui-copy';

interface TeeTime {
  id: string;
  courseName: string;
  courseId: string;
  dateTime: Date;
  originalDate?: string;
  price: number;
  capacity: number;
  bookedCount: number;
  availableSpots: number;
  customerTypeId?: string;
  pricingOptions?: Array<{
    customerTypeId: string;
    customerTypeName: string;
    price: number;
    color: string;
  }>;
  memberPlayers?: Array<{
    spotNumber: number;
    firstName: string;
    lastName: string;
    customerType: string;
  }>;
  dynamicPricing?: {
    direction: 'lower' | 'higher';
    percentRounded: number;
    underOnePercent: boolean;
  };
  promotion?: {
    type: 'percentage' | 'fixed';
    value: number;
    discountGreenFee?: boolean;
    discountCart?: boolean;
  };
  priceIncludesHalfCartAddon?: boolean;
}

interface BrandColors {
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  fontFamily: string;
  /** Matches modal note chrome when primary is neutral (e.g. black). */
  buttonColor?: string;
  headingTextBold?: boolean;
  headingTextItalic?: boolean;
}

interface TeeTimeCardProps {
  teeTime: TeeTime;
  onBook: (teeTime: TeeTime) => void;
  isBooking?: boolean;
  /** Logged-in member — hide Demand row and staff promo strip (posted price unchanged). */
  isMember?: boolean;
  brandColors?: BrandColors;
  preloadHalfCartWithGreenFee?: boolean;
}

export const TeeTimeCard: React.FC<TeeTimeCardProps> = ({
  teeTime,
  onBook,
  isBooking = false,
  isMember = false,
  brandColors: brandColorsProp,
  preloadHalfCartWithGreenFee = false,
}) => {
  const includesCartLabel =
    teeTime.priceIncludesHalfCartAddon !== undefined
      ? !!teeTime.priceIncludesHalfCartAddon
      : preloadHalfCartWithGreenFee;

  const tebox = useBookingTebox();
  const brandColors = brandColorsProp ?? {
    primaryColor: '#000000',
    secondaryColor: tebox.colors.textSecondary,
    textColor: tebox.colors.textPrimary,
    fontFamily: 'Inter',
  };
  const [isHovered, setIsHovered] = useState(false);

  const canBook = teeTime.availableSpots > 0 && !isBooking;

  const priceFootnoteDisplay = includesCartLabel ? 'Per player, incl. Cart' : 'Per player';
  const timeLabel = formatTime12hSingleLine(teeTime.dateTime);
  const timeDisplayParts = splitTime12h(teeTime.dateTime);

  const spotsLine =
    teeTime.availableSpots === 0
      ? 'Full'
      : teeTime.availableSpots === 1
        ? '1 spot'
        : `${teeTime.availableSpots} spots`;
  const spotsAvailablePhrase =
    teeTime.availableSpots === 0
      ? 'no spots available'
      : teeTime.availableSpots === 1
        ? '1 spot available'
        : `${teeTime.availableSpots} spots available`;

  const staffPromo = teeTime.promotion;
  const dp = staffPromo ? undefined : teeTime.dynamicPricing;
  const percentPhrase = dp
    ? dp.underOnePercent
      ? 'Under 1%'
      : `${dp.percentRounded}%`
    : '';
  const trendCaption =
    dp?.direction === 'lower'
      ? includesCartLabel
        ? 'lower than standard green fee rate'
        : 'lower than standard rate'
      : dp?.direction === 'higher'
        ? includesCartLabel
          ? 'above standard green fee rate'
          : 'above standard rate'
        : '';

  const handleActivate = () => {
    if (canBook) {
      onBook(teeTime);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!canBook) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  const ariaDynamic = isMember
    ? ''
    : staffPromo != null
      ? `, ${formatStaffPromoLine(staffPromo)}`
      : dp && percentPhrase
        ? `, ${percentPhrase} ${dp.direction === 'lower' ? 'below' : 'above'} standard ${includesCartLabel ? 'green fee ' : ''}rate`
        : '';

  const ariaPerPlayer = includesCartLabel
    ? 'per player, cart included with green fee'
    : 'per player';

  const vSpace = (token: string) => `calc(${token} * ${tebox.teeTimeCard.verticalCompactScale})`;
  const pad = vSpace(teboxDesignSystem.spacing.lg);
  const cardInsetY = `calc(${teboxDesignSystem.spacing.lg} * 0.8)`;
  const cardRadius = teboxDesignSystem.borderRadius.none;

  const cardInk = teboxDesignSystem.colors.text.primary;
  const cardSurface = teboxDesignSystem.colors.background.primary;
  const cardBackground = cardSurface;

  const demandHasTrend = staffPromo == null && dp != null && percentPhrase !== '';
  const showGuestDemandPromoUi = !isMember;
  const promoMeterActive = staffPromo != null;
  /** Guest: same meter row layout for promo (overrides demand) or dynamic demand trend. */
  const showGuestPricingMeterRow =
    showGuestDemandPromoUi && (promoMeterActive || demandHasTrend);

  const heroNumericStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    ...headingTextStyle({
      headingTextBold: brandColors.headingTextBold ?? false,
      headingTextItalic: brandColors.headingTextItalic ?? false,
    }),
    fontSize: tebox.teeTimeCard.heroHeaderNumericFontSize,
    lineHeight: 1.05,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    color: cardInk,
  };

  const timeMeridiemInlineStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    lineHeight: 1.35,
    color: tebox.colors.textSecondary,
    margin: 0,
    fontVariantNumeric: 'tabular-nums',
  };

  const metaStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    lineHeight: 1.35,
    color: cardInk,
  };

  const spotsChrome = bookingBrandInfoChromeStyles({
    primaryColor: brandColors.primaryColor,
    buttonColor: brandColors.buttonColor,
  });
  const spotsBadgeShellStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: teboxDesignSystem.spacing.sm,
    padding: `${vSpace(teboxDesignSystem.spacing.xs)} ${teboxDesignSystem.spacing.sm}`,
    borderRadius: teboxDesignSystem.borderRadius.sm,
    ...spotsChrome,
    flexShrink: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  /** Demand meter glyph — neutral chip (`gray[100]`) distinct from branded spots badge. */
  const demandTrendIconBackground = teboxDesignSystem.colors.gray[100];

  const spotsLabelStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
    lineHeight: 1.35,
    fontVariantNumeric: 'tabular-nums',
    color: cardInk,
    minWidth: 0,
  };

  const demandCirclePx = tebox.teeTimeCard.demandTrendCircleDiameterPx;
  const meterGlyphPx = Math.round(demandCirclePx * 0.5);

  const courseTitleStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: tebox.teeTimeCard.reservationCardCourseTitleFontWeight,
    fontSize: tebox.teeTimeCard.reservationCardCourseTitleFontSize,
    lineHeight: teboxDesignSystem.typography.h4.lineHeight,
    letterSpacing: teboxDesignSystem.typography.h4.letterSpacing,
    color: cardInk,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
    margin: 0,
  };

  const priceFootnoteOnlyStyle: React.CSSProperties = {
    fontFamily: bookingBrandFontFamilyVar,
    fontWeight: 500,
    fontSize: teboxDesignSystem.typography.caption.fontSize,
    lineHeight: 1.35,
    color: tebox.colors.textSecondary,
    margin: 0,
    marginTop: vSpace(teboxDesignSystem.spacing.xs),
    textAlign: 'right',
  };

  return (
    <div
      role="button"
      tabIndex={canBook ? 0 : -1}
      aria-disabled={!canBook}
      aria-label={
        canBook
          ? `Book ${teeTime.courseName} at ${timeLabel}, ${spotsAvailablePhrase}, ${formatMoney(teeTime.price)} ${ariaPerPlayer}${ariaDynamic}`
          : `Tee time unavailable, ${teeTime.courseName} at ${timeLabel}`
      }
      className="tee-time-card-shadow focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-[var(--tebox-focus-ring)]"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '400px',
        minHeight: tebox.teeTimeCard.cardMinHeight,
        height: tebox.teeTimeCard.cardHeight,
        boxSizing: 'border-box',
        borderRadius: `var(--brand-border-radius, ${cardRadius})`,
        border: `1px solid ${tebox.colors.border}`,
        backgroundColor: cardBackground,
        paddingLeft: pad,
        paddingRight: pad,
        paddingTop: cardInsetY,
        paddingBottom: cardInsetY,
        boxShadow:
          !canBook
            ? 'none'
            : isHovered
              ? teboxDesignSystem.shadows.sm
              : 'none',
        cursor: canBook ? 'pointer' : 'not-allowed',
        textAlign: 'left',
        color: cardInk,
        opacity: canBook ? 1 : 0.78,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            gap: teboxDesignSystem.spacing.md,
            width: '100%',
            flex: 1,
            minHeight: 0,
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              minWidth: 0,
              flex: '1 1 auto',
              gap: vSpace(teboxDesignSystem.spacing.sm),
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: teboxDesignSystem.spacing.xs,
                flexWrap: 'nowrap',
              }}
            >
              <span
                style={{
                  ...heroNumericStyle,
                  fontSize: tebox.teeTimeCard.clockFontSize,
                  lineHeight: 1.05,
                }}
              >
                {timeDisplayParts.clock}
              </span>
              <span style={timeMeridiemInlineStyle}>{timeDisplayParts.meridiem}</span>
            </div>
            <div style={courseTitleStyle}>{teeTime.courseName}</div>
            <div
              style={{ ...spotsBadgeShellStyle, alignSelf: 'flex-start' }}
              aria-label={spotsAvailablePhrase}
              data-tebox-brand-info-chrome="spots-pill"
              data-tebox-brand-info-bg-pct={bookingBrandInfoTintMixDefaultPercent}
              data-tebox-brand-info-border-pct={bookingBrandInfoChromeBorderMixPercent}
            >
              <span style={spotsLabelStyle}>{spotsLine}</span>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              textAlign: 'right',
              flexShrink: 0,
              maxWidth: '48%',
              alignSelf: 'stretch',
            }}
          >
            <span style={{ ...heroNumericStyle, lineHeight: 1.05 }}>
              ${formatMoney(teeTime.price)}
            </span>
            <p style={priceFootnoteOnlyStyle}>{priceFootnoteDisplay}</p>
            {showGuestPricingMeterRow ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: teboxDesignSystem.spacing.sm,
                  marginTop: 'auto',
                  paddingTop: vSpace(teboxDesignSystem.spacing.sm),
                }}
                title={
                  promoMeterActive ? formatStaffPromoLine(staffPromo!) : trendCaption || undefined
                }
              >
                <span style={{ ...metaStyle, fontWeight: 500, color: cardInk }}>
                  {promoMeterActive ? 'Promo' : 'Demand'}
                </span>
                <span
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: teboxDesignSystem.spacing.xs,
                    fontFamily: bookingBrandFontFamilyVar,
                    fontWeight: 500,
                    fontSize: teboxDesignSystem.typography.bodySmall.fontSize,
                    lineHeight: 1.2,
                    color: cardInk,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: demandCirclePx,
                      height: demandCirclePx,
                      borderRadius: teboxDesignSystem.borderRadius.full,
                      backgroundColor: demandTrendIconBackground,
                      flexShrink: 0,
                    }}
                  >
                    {promoMeterActive ? (
                      <PromoPricingGlyphIcon
                        sizePx={meterGlyphPx}
                        stroke={cardInk}
                        strokeWidth={2.25}
                      />
                    ) : (
                      <DemandTrendArrowIcon
                        direction={dp!.direction}
                        sizePx={meterGlyphPx}
                        stroke={cardInk}
                        strokeWidth={2.25}
                      />
                    )}
                  </span>
                  <span>
                    {promoMeterActive
                      ? formatStaffPromoMeterHeadline(staffPromo!)
                      : percentPhrase}
                  </span>
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
